import { flowResult } from 'mobx';
import { rootStore } from '../mobx/rootStore';
import { getAllProjectRawTasks, getAllRawTasks } from './taskServices';
import { RawTask } from '../mobx/interfaces/task';
import MobxTasksCacheService from './mobxTasksCacheService';
import MobxProjectsCacheService from './mobxProjectsCacheService';
import { getAllRawProjects } from './projectServices';
import { TaskModel } from '../mobx/models/task';
import { RawProject } from '../mobx/interfaces/project';

type SyncOptions = {
  loadCached?: boolean;
};

// Sync Tasks Data
export const syncTasks = async (options: SyncOptions = {}) => {
  const loadCached = typeof options.loadCached === 'boolean' ? options.loadCached : true;

  const lastTasksUpdatedDate = await MobxTasksCacheService.getInstance().getLastUpdatedTime();

  if (!lastTasksUpdatedDate) {
    // Fetch all tasks
    const allRawTasks = await getAllRawTasks();

    rootStore.taskStore.addTasks(allRawTasks);
    MobxTasksCacheService.getInstance().updateLastUpdatedTime();
  } else {
    if (loadCached) {
      await flowResult(rootStore.taskStore.updateWithIndexdb());
    }

    (async () => {
      // Background Fetch
      const allRawTasks = await getAllRawTasks(lastTasksUpdatedDate);

      const { newRawTasks, archivedTasks } = allRawTasks.reduce(
        (acc, task) => {
          if (task.isArchived) {
            acc.archivedTasks.push(task);
          } else {
            acc.newRawTasks.push(task);
          }

          return acc;
        },
        { newRawTasks: [] as RawTask[], archivedTasks: [] as RawTask[] }
      );

      const excludeFields: (keyof TaskModel)[] = ['comments', 'pullRequests', 'files', 'timeLogs'];
      rootStore.taskStore.syncList(newRawTasks, archivedTasks, { excludeFields });

      MobxTasksCacheService.getInstance().updateLastUpdatedTime();
    })();
  }
};

export const syncProjects = async (options: SyncOptions = {}) => {
  const loadCached = typeof options.loadCached === 'boolean' ? options.loadCached : true;

  const lastProjectsUpdatedDate = await MobxProjectsCacheService.getInstance().getLastUpdatedTime();

  if (!lastProjectsUpdatedDate) {
    await fetchProjectsAndUpdateStore(lastProjectsUpdatedDate);
  } else {
    if (loadCached) {
      await flowResult(rootStore.projectStore.updateWithIndexdb());
    }

    fetchProjectsAndUpdateStore(lastProjectsUpdatedDate);
  }
};

const fetchProjectsAndUpdateStore = async (lastProjectsUpdatedDate: Date | null) => {
  const allRawProjects = await getAllRawProjects();

  let updatedProjects: RawProject[] = [];

  if (lastProjectsUpdatedDate) {
    updatedProjects = allRawProjects.filter((project) => new Date(project.updatedOn) > lastProjectsUpdatedDate);

    const currentProjectIds = rootStore.projectStore.projects.map((project) => project.id);
    const completelyNewProjects = allRawProjects.filter((project) => !currentProjectIds.includes(project.id));

    if (completelyNewProjects.length > 0) {
      await syncTasksForProjects(completelyNewProjects.map((project) => project.id));
    }
  } else {
    rootStore.projectStore.setProjects(allRawProjects);
  }

  const allProjectIds = allRawProjects.map((project) => project.id);
  const removedProjects = rootStore.projectStore.projects.filter((project) => !allProjectIds.includes(project.id));

  rootStore.projectStore.syncList(updatedProjects, removedProjects);
  rootStore.projectStore.updateWithFirebase();

  MobxProjectsCacheService.getInstance().updateLastUpdatedTime();
};

export const syncTasksForProjects = async (projectIds: string[]) => {
  const allRawTasks = await getAllProjectRawTasks(projectIds);
  rootStore.taskStore.addTasks(allRawTasks);
};
